import { useCallback } from 'react';
import useAnalytics from '~/common/useAnalytics';
import { REFERRALS_PAGE_VIEW } from '~/components/Referral/components/ReferralsPage/analyticsEvents';
import { REFERRAL_QUERY_PARAMS } from '~/components/Referral/components/ReferralsPage/constants';
import { IEventProperties } from '~/services/analytics/analytics.service';
import { trackError } from '~/services/logger/error.utils';
import { logInfo, logError } from '~/services/logger/logger.utils';

type LogReferrals = {
    message: string;
    context?: { [key: string]: any };
};

type TrackReferralsError = {
    error: Error;
    message?: string;
    context?: { [key: string]: any };
};

const REFERRALS_VERSION = 2.0;
const REFERRALS_MESSAGE_PREFIX = '[REFERRALS]';
const commonContext = {
    referrals_version: REFERRALS_VERSION
};

const getLoadedEventPropsFromQueryParams = () => {
    // for each param in referral query params, get the value from the query params
    const queryParams = new URLSearchParams(window.location.search);

    const eventProps: Partial<IEventProperties> = {};

    const source = queryParams.get(REFERRAL_QUERY_PARAMS.SOURCE);
    if (source == 'hm') {
        eventProps.source = 'happy moment';

        const actionType = queryParams.get(REFERRAL_QUERY_PARAMS.ACTION_TYPE);

        if (actionType) {
            eventProps.object_type = actionType;
            eventProps.action_type = 'ai_emails';
        }
    }
    return eventProps;
};

export const useReferralsTracking = () => {
    const { track } = useAnalytics();

    const logReferralsInfo = useCallback(
        ({ message, context = {} }: LogReferrals) => {
            logInfo(`${REFERRALS_MESSAGE_PREFIX} ${message}`, {
                ...commonContext,
                ...context
            });
        },
        []
    );

    const logReferralsError = useCallback(
        ({ message, context = {} }: LogReferrals) => {
            logError(`${REFERRALS_MESSAGE_PREFIX} ${message}`, {
                ...commonContext,
                ...context
            });
        },
        []
    );

    const _trackReferralsError = useCallback(
        ({ error, message, context }: TrackReferralsError) => {
            trackError({
                error,
                message: `${REFERRALS_MESSAGE_PREFIX} ${message}`,
                context: {
                    ...commonContext,
                    ...context
                }
            });
        },
        []
    );

    const trackReferralsPageView = useCallback(() => {
        const loadedEventProps = getLoadedEventPropsFromQueryParams();
        track(REFERRALS_PAGE_VIEW, { action: 'loaded', ...loadedEventProps });
        logReferralsInfo({ message: `loaded: ${REFERRALS_PAGE_VIEW}` });
    }, [logReferralsInfo, track]);

    const trackReferralsSuccess = useCallback(
        ({
            message,
            context
        }: {
            message: string;
            context?: LogReferrals['context'];
        }) => {
            track(message, { action: 'success', ...context });
            logReferralsInfo({
                message: `success: ${message}`,
                context
            });
        },
        [logReferralsInfo, track]
    );

    const trackReferralsError = useCallback(
        ({
            message,
            context,
            error
        }: {
            message: string;
            context?: LogReferrals['context'];
            error: Error;
        }) => {
            track(message, { action: 'error', ...context });
            _trackReferralsError({
                error,
                message: `error: ${message}`,
                context
            });
        },
        [_trackReferralsError, track]
    );

    return {
        logReferralsInfo,
        logReferralsError,
        trackReferralsPageView,
        trackReferralsSuccess,
        trackReferralsError
    };
};
